/**
 * Thesis Fit — React Query Hooks for Server Sync
 *
 * Provides hooks for fetching and saving thesis profiles
 * to the thesis-profile edge function.
 *
 * Uses `action` field in body since supabase.functions.invoke always sends POST.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { reportError } from '@/lib/errors';
import { useThesisStore } from './thesis-store';
import type { ThesisProfile, ThesisArchetype } from './types';

// =============================================================================
// Query Key
// =============================================================================

const THESIS_KEY = 'thesis-profile';

// =============================================================================
// API Helpers
// =============================================================================

interface ThesisProfileRow {
  id: string;
  investor_user_id: string;
  name: string;
  archetype: string;
  weights: Record<string, number>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function rowToProfile(row: ThesisProfileRow): ThesisProfile {
  return {
    id: row.id,
    investorId: row.investor_user_id,
    name: row.name,
    archetype: row.archetype as ThesisArchetype,
    weights: row.weights as ThesisProfile['weights'],
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function fetchThesisProfile(): Promise<ThesisProfile | null> {
  const { data, error } = await supabase.functions.invoke('thesis-profile', {
    body: { action: 'get' },
  });

  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Failed to fetch thesis profile');

  return data.data ? rowToProfile(data.data) : null;
}

async function saveThesisProfile(
  profile: Pick<ThesisProfile, 'name' | 'archetype' | 'weights'>,
): Promise<ThesisProfile> {
  const { data, error } = await supabase.functions.invoke('thesis-profile', {
    body: {
      action: 'save',
      name: profile.name,
      archetype: profile.archetype,
      weights: profile.weights,
    },
  });

  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Failed to save thesis profile');

  return rowToProfile(data.data);
}

async function deleteThesisProfile(): Promise<void> {
  const { data, error } = await supabase.functions.invoke('thesis-profile', {
    body: { action: 'delete' },
  });

  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Failed to delete thesis profile');
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch thesis profile from server, hydrate Zustand on success.
 */
export function useThesisQuery(investorId: string | undefined) {
  return useQuery({
    queryKey: [THESIS_KEY, investorId],
    queryFn: async () => {
      const profile = await fetchThesisProfile();
      // Hydrate Zustand store
      if (profile) {
        useThesisStore.getState().setThesis(profile);
      }
      return profile;
    },
    enabled: !!investorId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Save thesis profile to server, update cache + Zustand.
 */
export function useThesisMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveThesisProfile,
    onSuccess: (saved) => {
      // Update Zustand store with server response (has real ID)
      useThesisStore.getState().setThesis(saved);
      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: [THESIS_KEY] });
    },
    onError: (err) => {
      reportError(err, { context: 'useThesisMutation.save', level: 'warning' });
    },
  });
}

/**
 * Delete thesis profile from server.
 */
export function useThesisDeleteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteThesisProfile,
    onSuccess: () => {
      useThesisStore.getState().clearThesis();
      queryClient.invalidateQueries({ queryKey: [THESIS_KEY] });
    },
    onError: (err) => {
      reportError(err, { context: 'useThesisDeleteMutation', level: 'warning' });
    },
  });
}
